import { Controller } from "@hotwired/stimulus"
import { pickerTexts, peopleRows, selectedRows, checkInviteEmail, attachWhyTips, newMemberColor, newChipTag } from "../lib/member_picker"

export default class extends Controller {
  static targets = [
    "addMembersBtn", "popover", "popoverSearch", "popoverList",
    "selectedList", "selectedCount", "pickerCompany",
    "pickerInviteToggle", "pickerInviteRow", "pickerInviteInput", "pickerInviteError", "pickerInviteBtn",
    "inviteModal", "inviteModalCompany", "inviteEmailInput", "inviteEmailError", "inviteSubmitBtn",
    "memberChips", "badge", "hiddenEntry"
  ]

  static values = {
    membersUrl: String,
    validateEmailUrl: String,
    companyId: String,
    companyName: String,
    initialMembers: { type: Array, default: [] },   // legacy: used by Manage Team modal
    autoMembers: { type: Array, default: [] },       // non-removable (captain/commander/creator)
    regionalMembers: { type: Array, default: [] },   // conditional on project region; removable
    initialRegionId: String,                         // initial project region (from tag_name)
    initialAdminAssistantEmail: String,              // AA selected in the Details step
    initialAdminAssistantLabel: String,              // display label (e.g. "Name - email")
    collapseThreshold: { type: Number, default: 0 }  // 0 = no collapse
  }

  connect() {
    this._members = []
    this._membersLoaded = false
    this._selected = new Set()
    // Track display label for each selected email (so collapse/re-render keeps the name)
    this._selectedMeta = new Map()
    // Emails rendered as non-removable (no × button) — captain/commander/creator
    this._autoEmails = new Set()
    // Emails sourced from regional-access matching — managed by region-changed listener
    this._regionalEmails = new Set()
    // Emails sourced from Admin Assistant — managed by aa-changed listener (non-removable)
    this._aaEmails = new Set()
    // Current AA email + whether we added a chip for it (vs. marking an existing one)
    this._aaEmail = null
    this._aaAddedChip = false
    // Current region (used to filter regional_access suggestions)
    this._currentRegionId = this.initialRegionIdValue ? String(this.initialRegionIdValue) : null
    this._expanded = false

    this._onClickOutside = (e) => {
      // A row the pop-up just redrew is no longer in the page: not an outside click.
      if (!e.target.isConnected) return
      if (this.hasPopoverTarget && !this.popoverTarget.contains(e.target) &&
          (!this.hasAddMembersBtnTarget || !this.addMembersBtnTarget.contains(e.target))) {
        this._closePopover()
      }
    }
    document.addEventListener("click", this._onClickOutside)
    // The hover tips on Auto / AA / New tags in the Add Members pop-up.
    this._whyTips = attachWhyTips(this.hasPopoverTarget ? this.popoverTarget : null)

    // Seed auto members (always selected, always non-removable)
    this._autoWhy = new Map() // email → why they're on automatically (hover)
    this.autoMembersValue.forEach(m => {
      const label = m.name || m.email
      this._selected.add(m.email)
      this._selectedMeta.set(m.email, label)
      this._autoEmails.add(m.email)
      if (m.why) this._autoWhy.set(m.email, m.why)
    })

    // Legacy initial members (Manage Team modal) — treated as normal (removable) chips
    this.initialMembersValue.forEach(m => {
      const label = m.name || m.email
      this._selected.add(m.email)
      this._selectedMeta.set(m.email, label)
    })

    // Apply regional filter for whatever region we know about at connect time
    this._applyRegionalFilter()

    // Seed initial AA selection (from the Details step select on first paint)
    if (this.initialAdminAssistantEmailValue) {
      this._updateAAChip(
        this.initialAdminAssistantEmailValue,
        this.initialAdminAssistantLabelValue || null,
        { rerender: false }
      )
    }

    this._renderChips()
    this._updateBadge()
    this._syncHiddenEntry()

    if (this.companyIdValue) {
      this._loadMembers(this.companyIdValue)
    } else {
      this._membersLoaded = true
    }

    // Listen for region updates from the wizard (fired by tagNameSelected / propertyCreated)
    this._onRegionChanged = (e) => {
      const rid = e.detail?.regionId
      this._currentRegionId = rid ? String(rid) : null
      this._applyRegionalFilter()
      this._renderChips()
      this._updateBadge()
      this._syncHiddenEntry()
    }
    document.addEventListener("owner-members:region-changed", this._onRegionChanged)

    // Listen for Admin Assistant updates from the wizard (Details step select)
    this._onAAChanged = (e) => {
      this._updateAAChip(e.detail?.email || null, e.detail?.label || null)
    }
    document.addEventListener("owner-members:aa-changed", this._onAAChanged)
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
    document.removeEventListener("keydown", this._onPickerKey)
    this._whyTips?.destroy()
    if (this._onRegionChanged) {
      document.removeEventListener("owner-members:region-changed", this._onRegionChanged)
    }
    if (this._onAAChanged) {
      document.removeEventListener("owner-members:aa-changed", this._onAAChanged)
    }
  }

  // Reacts to Admin Assistant selection in the Details step. If the chosen AA
  // email is already selected for another reason (auto/regional/manual), we
  // just mark it as also-AA; otherwise we add a chip. When the AA changes or
  // is cleared, we undo the chip we added — but leave chips that came from
  // another source alone.
  _updateAAChip(newEmail, newLabel, { rerender = true } = {}) {
    const oldEmail = this._aaEmail
    const normalized = newEmail ? String(newEmail).trim().toLowerCase() : null
    if (normalized === oldEmail) return

    if (oldEmail) {
      this._aaEmails.delete(oldEmail)
      if (this._aaAddedChip) {
        this._selected.delete(oldEmail)
        this._selectedMeta.delete(oldEmail)
      }
    }

    this._aaEmail = normalized
    this._aaAddedChip = false

    if (normalized) {
      this._aaEmails.add(normalized)
      if (!this._selected.has(normalized)) {
        this._selected.add(normalized)
        this._selectedMeta.set(normalized, newLabel || normalized)
        this._aaAddedChip = true
      }
    }

    if (rerender) {
      this._renderChips()
      this._updateBadge()
      this._syncHiddenEntry()
    }
  }

  // Recomputes regional-access chips based on the current region.
  // Only manages chips this controller itself added via regional matching —
  // manually-added and auto chips are left alone, even if the same email also
  // happens to be in the regional list.
  _applyRegionalFilter() {
    this._regionalEmails.forEach(email => {
      if (this._autoEmails.has(email)) return
      this._selected.delete(email)
      this._selectedMeta.delete(email)
    })
    this._regionalEmails.clear()

    if (!this._currentRegionId) return

    this.regionalMembersValue.forEach(m => {
      const matches = (m.region_ids || []).map(String).includes(this._currentRegionId)
      if (!matches) return
      // Don't claim this email as "regional-managed" if it's already present
      // from another source (manual add or auto). That way switching regions
      // later won't yank a user the operator added deliberately.
      const alreadyPresent = this._selected.has(m.email)
      this._selected.add(m.email)
      this._selectedMeta.set(m.email, m.name || m.email)
      if (!alreadyPresent) this._regionalEmails.add(m.email)
    })
  }

  // ── Popover ─────────────────────────────────────────────────────────

  togglePopover() {
    if (!this.hasPopoverTarget) return
    if (this.popoverTarget.classList.contains("hidden")) {
      this._openPopover()
    } else {
      this._closePopover()
    }
  }

  // The Add Members pop-up (Phelipe 2026-10-08): a centered window with the
  // company's people on the left and who's on the project on the right.
  _openPopover() {
    if (this.hasPickerCompanyTarget) this.pickerCompanyTarget.textContent = this.companyNameValue || ""
    if (this.hasPopoverSearchTarget) this.popoverSearchTarget.value = ""
    this._renderPopoverList("")
    this._renderSelectedList()
    if (this.hasPickerInviteRowTarget) {
      this.pickerInviteRowTarget.classList.add("hidden")
      this.pickerInviteInputTarget.value = ""
      this._showPickerInviteError("")
    }
    this.popoverTarget.classList.remove("hidden")
    this._onPickerKey = (e) => { if (e.key === "Escape") this._closePopover() }
    document.addEventListener("keydown", this._onPickerKey)
    if (this.hasPopoverSearchTarget) this.popoverSearchTarget.focus()
  }

  _closePopover() {
    this._whyTips?.hide()
    if (this.hasPopoverTarget) this.popoverTarget.classList.add("hidden")
    document.removeEventListener("keydown", this._onPickerKey)
  }

  closePicker() {
    this._closePopover()
  }

  // Only a click on the dimmed backdrop itself closes it.
  closePickerFromBackdrop(event) {
    if (event.target === event.currentTarget) this._closePopover()
  }

  // ── Invite New Member, inside the pop-up (Phelipe 2026-10-08) ─────
  // The email box opens right under the list; add one after another. Each
  // one lands on the right in blue with a "New" tag.
  openPickerInvite() {
    if (!this.hasPickerInviteRowTarget) return this.showInviteModal()
    this.pickerInviteRowTarget.classList.remove("hidden")
    this.pickerInviteInputTarget.focus()
  }

  pickerInviteKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.submitPickerInvite()
    } else if (event.key === "Escape") {
      event.stopPropagation() // closes the email box, not the whole pop-up
      this.pickerInviteRowTarget.classList.add("hidden")
    }
  }

  async submitPickerInvite() {
    const texts = pickerTexts(this.popoverTarget)
    const input = this.pickerInviteInputTarget
    const btn = this.pickerInviteBtnTarget
    const email = input.value.trim().toLowerCase()
    this._showPickerInviteError("")
    btn.disabled = true
    btn.textContent = texts.checking
    const error = await checkInviteEmail(email, { selected: this._selected, validateUrl: this.validateEmailUrlValue, texts })
    btn.disabled = false
    btn.textContent = texts.add
    if (error) return this._showPickerInviteError(error)

    this._invited ||= new Set()
    this._invited.add(email)
    this._selected.add(email)
    this._selectedMeta.set(email, email)
    this._expanded = true // show the new chip under the card right away
    this._renderChips()
    this._updateBadge()
    this._syncHiddenEntry()
    this._renderSelectedList()
    input.value = ""
    input.focus()
  }

  _showPickerInviteError(message) {
    if (!this.hasPickerInviteErrorTarget) return
    this.pickerInviteErrorTarget.textContent = message
    this.pickerInviteErrorTarget.classList.toggle("hidden", !message)
  }

  _pickerOpen() {
    return this.hasPopoverTarget && !this.popoverTarget.classList.contains("hidden")
  }

  filterMembers() {
    const query = this.popoverSearchTarget.value.trim().toLowerCase()
    this._renderPopoverList(query)
  }

  _renderPopoverList(query) {
    if (!this.hasPopoverListTarget) return
    this.popoverListTarget.innerHTML = peopleRows({
      members: this._members, selected: this._selected, locked: this._lockedBadges(), query,
      loaded: this._membersLoaded, ctrl: "owner-members", texts: pickerTexts(this.popoverTarget)
    })
  }

  _renderSelectedList() {
    if (!this.hasSelectedListTarget) return
    const locked = this._lockedBadges()
    const entries = Array.from(this._selected).map((email) => ({
      email, label: this._selectedMeta.get(email) || email, lock: locked.get(email), isNew: this._invited?.has(email)
    }))
    this.selectedListTarget.innerHTML = selectedRows({ entries, ctrl: "owner-members", texts: pickerTexts(this.popoverTarget) })
    if (this.hasSelectedCountTarget) this.selectedCountTarget.textContent = entries.length ? String(entries.length) : ""
  }

  // People who can't be taken off here: the Admin Assistant (set in Details)
  // and the automatic admins.
  _lockedBadges() {
    const texts = pickerTexts(this.hasPopoverTarget ? this.popoverTarget : null)
    const locked = new Map()
    this._autoEmails.forEach((e) => locked.set(e, { text: texts.auto, why: this._autoWhy?.get(e) || "", title: texts.autoTitle }))
    this._aaEmails.forEach((e) => locked.set(e, { text: "AA", why: texts.aaWhy, title: texts.aaTitle }))
    return locked
  }

  // ── Member toggle ───────────────────────────────────────────────────

  toggleMember(event) {
    const checkbox = event.currentTarget
    const email = checkbox.value
    const name = checkbox.dataset.name

    // Auto and AA chips can't be unchecked
    if (this._autoEmails.has(email) || this._aaEmails.has(email)) {
      checkbox.checked = true
      return
    }

    if (checkbox.checked) {
      this._selected.add(email)
      this._selectedMeta.set(email, name || email)
      this._expanded = true // show newly-added chips immediately
    } else {
      this._selected.delete(email)
      this._selectedMeta.delete(email)
      this._regionalEmails.delete(email)
    }
    this._renderChips()
    this._updateBadge()
    this._syncHiddenEntry()
    this._renderSelectedList()
  }

  // ── Invite Modal ────────────────────────────────────────────────────

  showInviteModal() {
    this._closePopover()
    if (!this.hasInviteModalTarget) return

    if (this.hasInviteModalCompanyTarget) {
      this.inviteModalCompanyTarget.textContent = this.companyNameValue || ""
    }

    if (this.hasInviteEmailInputTarget) this.inviteEmailInputTarget.value = ""
    if (this.hasInviteEmailErrorTarget) {
      this.inviteEmailErrorTarget.classList.add("hidden")
      this.inviteEmailErrorTarget.textContent = ""
    }
    if (this.hasInviteSubmitBtnTarget) {
      this.inviteSubmitBtnTarget.disabled = false
      this.inviteSubmitBtnTarget.textContent = "Add"
    }

    this.inviteModalTarget.classList.remove("hidden")
    setTimeout(() => this.inviteEmailInputTarget?.focus(), 50)
  }

  closeInviteModal() {
    if (this.hasInviteModalTarget) {
      this.inviteModalTarget.classList.add("hidden")
    }
  }

  inviteEmailKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.submitInviteEmail()
    } else if (event.key === "Escape") {
      this.closeInviteModal()
    }
  }

  async submitInviteEmail() {
    if (!this.hasInviteEmailInputTarget) return

    const email = this.inviteEmailInputTarget.value.trim().toLowerCase()

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this._showInviteError("Please enter a valid email address.")
      return
    }

    if (this._selected.has(email)) {
      this._showInviteError("This member has already been added.")
      return
    }

    if (this.validateEmailUrlValue) {
      this._setInviteLoading(true)
      try {
        const url = `${this.validateEmailUrlValue}?email=${encodeURIComponent(email)}`
        const resp = await fetch(url, { headers: { "Accept": "application/json" } })
        if (resp.ok) {
          const data = await resp.json()
          if (!data.is_valid) {
            this._setInviteLoading(false)
            this._showInviteError("This email address doesn't exist. Please double-check for typos and try again.")
            return
          }
        }
      } catch { /* proceed if validation endpoint fails */ }
      this._setInviteLoading(false)
    }

    this._invited ||= new Set()
    this._invited.add(email)
    this._selected.add(email)
    this._selectedMeta.set(email, email)
    this._expanded = true // show newly-invited chip immediately
    this._renderChips()
    this._updateBadge()
    this._syncHiddenEntry()
    this.closeInviteModal()
  }

  _showInviteError(msg) {
    if (!this.hasInviteEmailErrorTarget) return
    this.inviteEmailErrorTarget.textContent = msg
    this.inviteEmailErrorTarget.classList.remove("hidden")
    this.inviteEmailInputTarget?.classList.add("border-red-400", "ring-1", "ring-red-400")
  }

  _setInviteLoading(loading) {
    if (!this.hasInviteSubmitBtnTarget) return
    this.inviteSubmitBtnTarget.disabled = loading
    this.inviteSubmitBtnTarget.textContent = loading ? "Validating..." : "Add"
  }

  // ── Load members ────────────────────────────────────────────────────

  async _loadMembers(companyId) {
    if (!this.membersUrlValue) { this._membersLoaded = true; return }
    try {
      const url = `${this.membersUrlValue}?id=${companyId}`
      const resp = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!resp.ok) return
      const data = await resp.json()
      if (Array.isArray(data)) this._members = data
    } catch { /* ignore */ } finally {
      // Loaded or not, stop saying "Loading" (a failed load reads as no one).
      this._membersLoaded = true
      if (this._pickerOpen()) this._renderPopoverList(this.hasPopoverSearchTarget ? this.popoverSearchTarget.value : "")
    }
  }

  // ── Chips ─────────────────────────────────────────────────────────────

  _renderChips() {
    if (!this.hasMemberChipsTarget) return

    const entries = Array.from(this._selected).map(email => ({
      email,
      label: this._selectedMeta.get(email) || email
    }))
    const threshold = this.collapseThresholdValue
    const shouldCollapse = threshold > 0 && entries.length > threshold
    const hiddenCount = shouldCollapse ? entries.length - threshold : 0

    this.memberChipsTarget.innerHTML = ""

    // Always render every chip so downstream code (review step, form collection)
    // can see them, but visually hide chips past the threshold when collapsed.
    entries.forEach(({ email, label }, idx) => {
      const hidden = shouldCollapse && !this._expanded && idx >= threshold
      const isAuto = this._autoEmails.has(email)
      const isAA = this._aaEmails.has(email)
      this.memberChipsTarget.appendChild(this._buildChip(label, email, hidden, isAuto, isAA))
    })

    if (shouldCollapse) {
      this.memberChipsTarget.appendChild(
        this._buildExpandToggle(hiddenCount, !this._expanded)
      )
    }
  }

  _buildChip(label, email, hidden = false, isAuto = false, isAA = false) {
    const chip = document.createElement("span")
    chip.dataset.chipEmail = email
    chip.className = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium"
    // Inline `display: none` beats both .hidden and .inline-flex at equal
    // specificity — using the Tailwind .hidden class here gets overridden by
    // .inline-flex and the chip stays visible.
    const displayRule = hidden ? "display:none;" : ""
    // Someone invited by email is pink with a "New" tag (Phelipe 2026-10-08).
    const isNew = this._invited?.has(email)
    const color = isNew ? newMemberColor("#16a34a") : "#16a34a"
    chip.style.cssText = `${displayRule}font-size:14px;color:${color};background:${color}12;border:1px solid ${color}4D;`

    // Non-removable chips: auto (captain/commander/creator) or AA (set via
    // the Details step). The AA chip also gets a small "AA" badge since the
    // user might not recognize the email otherwise.
    const nonRemovable = isAuto || isAA
    const aaBadge = isAA
      ? `<span class="inline-flex items-center rounded bg-gray-100 text-text-muted px-1" style="font-size:13px;line-height:1.2;" title="Admin Assistant — set in Details step">AA</span>`
      : ""

    if (nonRemovable) {
      chip.title = isAA
        ? "Admin Assistant — change in the Details step"
        : (this._autoWhy?.get(email) || "Automatically included — admin role")
      chip.innerHTML = `${this._esc(label)}${aaBadge}`
    } else {
      chip.innerHTML = `
        ${this._esc(label)}${isNew ? newChipTag(this.hasPopoverTarget ? pickerTexts(this.popoverTarget) : null, color) : ""}
        <button type="button" data-action="owner-members#removeChip" data-email="${this._esc(email)}"
                class="ml-0.5 hover:text-red-500 cursor-pointer">&times;</button>
      `
    }
    return chip
  }

  _buildExpandToggle(hiddenCount, collapsed) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.dataset.action = "click->owner-members#toggleExpand"
    btn.className = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium text-gray-600 bg-gray-100 border border-gray-200 hover:bg-gray-200 transition-colors cursor-pointer"
    btn.style.cssText = "font-size:14px;"
    btn.textContent = collapsed ? `+${hiddenCount} more` : "Show less"
    return btn
  }

  toggleExpand() {
    this._expanded = !this._expanded
    this._renderChips()
  }

  removeChip(event) {
    event.preventDefault()
    event.stopPropagation()
    const email = event.currentTarget.dataset.email
    // Guard: auto and AA chips aren't removable
    if (this._autoEmails.has(email) || this._aaEmails.has(email)) return
    this._selected.delete(email)
    this._selectedMeta.delete(email)
    this._regionalEmails.delete(email)
    this._renderChips()
    this._updateBadge()
    this._syncHiddenEntry()

    if (this.hasPopoverTarget && !this.popoverTarget.classList.contains("hidden")) {
      const cb = this.popoverTarget.querySelector(`input[type="checkbox"][value="${CSS.escape(email)}"]`)
      if (cb) cb.checked = false
      this._renderSelectedList()
    }
  }

  _syncHiddenEntry() {
    if (!this.hasHiddenEntryTarget) return
    // Exclude AA-only emails — the admin_assistant callback handles membership
    // for the selected AA (including when the email isn't a registered user).
    // Chips that are also auto or regional stay in the list via those paths.
    const emails = Array.from(this._selected).filter(email => {
      const onlyAA = this._aaEmails.has(email) &&
                     !this._autoEmails.has(email) &&
                     !this._regionalEmails.has(email)
      return !onlyAA
    })
    this.hiddenEntryTarget.value = emails.join(" ")
  }

  _updateBadge() {
    if (!this.hasBadgeTarget) return
    const count = this._selected.size
    this.badgeTarget.textContent = `${count} member${count !== 1 ? "s" : ""}`
  }

  // ── Public API (read by parent controller on save) ──────────────────

  getSelectedEmails() {
    return Array.from(this._selected)
  }

  // ── Utilities ─────────────────────────────────────────────────────────

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
